import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useDropzone } from "react-dropzone";
import { useTranslate } from "@tolgee/react";
import VideoFileOutlinedIcon from "@mui/icons-material/VideoFileOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import useVideoTranslation from "../hooks/useVideoTranslation";
import useToolProject from "../hooks/useToolProject";
import { useAuthService } from "../Context/AuthContext";
import { useNotify } from "../Helpers/Notifications";
import { PRODUCT_SLUG_VIDEO_TOOL, PLATFORM_URL, FEATURE_FLAGS } from "../Constants/constantValues";
import LocalStorage from "../Helpers/LocalStorage";
import LanguageBtn from "../Components/Auth Components/LanguageBtn";
import { getVideoToolLanguagesAPI } from "../API/users/getTranslationsLanguages";
import ProjectSelector from "../Components/GenericComponents/ProjectSelector";

const ACCENT = "#00BFA5";

const VideoIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="4" width="15" height="16" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17 9l5-3v12l-5-3V9z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ACCEPT = {
  "video/mp4": [".mp4"],
  "video/quicktime": [".mov"],
  "video/x-matroska": [".mkv"],
  "video/webm": [".webm"],
  "video/x-msvideo": [".avi"],
};

const isTerminal = (status) => ["completed", "failed", "rejected"].includes(status);
const isInFlight = (status) => ["uploading", "submitted", "processing"].includes(status);

const MAX_VIDEO_CONTEXT_LENGTH = 500;
const MAX_GLOSSARY_ROWS = 200;
const MAX_GLOSSARY_FIELD_LENGTH = 200;

const VideoTranslationToolPage = () => {
  const [file, setFile] = useState(null);
  const [sourceLang, setSourceLang] = useState("");
  const [targetLangs, setTargetLangs] = useState([]);
  const [includeSubtitles, setIncludeSubtitles] = useState(false);
  const [includeSrtExport, setIncludeSrtExport] = useState(false);
  const [showContextGlossary, setShowContextGlossary] = useState(false);
  const [videoContext, setVideoContext] = useState("");
  const [glossary, setGlossary] = useState([]);
  const glossaryIdRef = useRef(0);
  const { t } = useTranslate();
  const { platformVideoTool, authHasCompleted, checkSubscriptions } = useAuthService();
  const { projectId, projects, setSelectedId, isMultiple } = useToolProject(PRODUCT_SLUG_VIDEO_TOOL);
  const { notifyError } = useNotify();
  const [videoToolLanguages, setVideoToolLanguages] = useState([]);
  const [maxTargetLanguages, setMaxTargetLanguages] = useState(1);

  const STATUS_LABELS = {
    uploading: t("video_tool.status_uploading"),
    submitted: t("video_tool.status_submitted"),
    processing: t("video_tool.status_processing"),
    completed: t("video_tool.status_completed"),
    failed: t("video_tool.status_failed"),
    rejected: t("video_tool.status_rejected"),
  };

  useEffect(() => {
    if (videoToolLanguages.length !== 0) return;
    getVideoToolLanguagesAPI(
      (response) => {
        setVideoToolLanguages(response.data.languages);
        setMaxTargetLanguages(response.data.max_target_languages || 1);
      },
      () => {}
    );
  }, [videoToolLanguages.length]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") checkSubscriptions();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [checkSubscriptions]);

  const { jobs, startTranslation, reset, downloadJob } = useVideoTranslation(projectId);

  const hasJobs = jobs.length > 0;
  const allDone = hasJobs && jobs.every((j) => isTerminal(j.status));

  const onDrop = useCallback(
    (accepted, rejected) => {
      if (rejected.length > 0) {
        notifyError(t("video_tool.invalid_file"));
        return;
      }
      if (accepted.length > 0) setFile(accepted[0]);
    },
    [notifyError, t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    maxFiles: 1,
    disabled: hasJobs,
  });

  const availableLangs = useMemo(
    () => videoToolLanguages.filter((l) => l.locale !== sourceLang && !targetLangs.includes(l.locale)),
    [videoToolLanguages, sourceLang, targetLangs]
  );

  const addTargetLang = useCallback((locale) => {
    if (!locale) return;
    setTargetLangs((prev) => (prev.includes(locale) || prev.length >= maxTargetLanguages ? prev : [...prev, locale]));
  }, [maxTargetLanguages]);

  const removeTargetLang = useCallback((locale) => {
    setTargetLangs((prev) => prev.filter((l) => l !== locale));
  }, []);

  useEffect(() => {
    setTargetLangs((prev) => prev.filter((l) => l !== sourceLang));
  }, [sourceLang]);

  const languageName = useCallback(
    (locale) => videoToolLanguages.find((l) => l.locale === locale)?.name || locale,
    [videoToolLanguages]
  );

  const handleTranslate = useCallback(() => {
    if (!file || !sourceLang || targetLangs.length === 0) return;
    startTranslation(file, sourceLang, targetLangs, includeSubtitles, includeSrtExport, videoContext, glossary, "clone");
  }, [file, sourceLang, targetLangs, includeSubtitles, includeSrtExport, videoContext, glossary, startTranslation]);

  const handleReset = useCallback(() => {
    reset();
    setFile(null);
    setSourceLang("");
    setTargetLangs([]);
    setIncludeSubtitles(false);
    setIncludeSrtExport(false);
    setShowContextGlossary(false);
    setVideoContext("");
    setGlossary([]);
  }, [reset]);

  const addGlossaryRow = useCallback(() => {
    setGlossary((prev) => {
      if (prev.length >= MAX_GLOSSARY_ROWS) return prev;
      glossaryIdRef.current += 1;
      return [...prev, { id: glossaryIdRef.current, term: "", translation: "" }];
    });
  }, []);

  const removeGlossaryRow = useCallback((id) => {
    setGlossary((prev) => prev.filter((row) => row.id !== id));
  }, []);

  const updateGlossaryRow = useCallback((id, field, value) => {
    setGlossary((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  }, []);

  if (authHasCompleted === null || platformVideoTool === null) {
    return <PageWrapper />;
  }

  if (!platformVideoTool?.enabled) {
    return (
      <PageWrapper>
        <Content>
          <NoAccessCard>
            <NoAccessIcon>
              <span style={{ color: `${ACCENT}80` }}><VideoIcon /></span>
            </NoAccessIcon>
            <NoAccessTitle>{t("video_tool.title")}</NoAccessTitle>
            <NoAccessMessage>{t("video_tool.no_access_message")}</NoAccessMessage>
            <NoAccessLink onClick={async () => {
              const currentToken = LocalStorage.getAuthenticationToken();
              if (!currentToken) { window.open(PLATFORM_URL); return; }
              const { getAuthBridgeExchangeToken } = await import("../API/authBridge");
              const exchangeToken = await getAuthBridgeExchangeToken(currentToken);
              if (exchangeToken) {
                if (window.electron?.openExternalUrl) {
                  window.electron.openExternalUrl(`${PLATFORM_URL}/login?bridge=${exchangeToken}`);
                } else {
                  window.open(`${PLATFORM_URL}/login?bridge=${exchangeToken}`);
                }
              } else {
                window.open(PLATFORM_URL);
              }
            }}>
              {t("video_tool.go_to_platform")}
            </NoAccessLink>
          </NoAccessCard>
        </Content>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Content>
        <TitleArea>
          <TitleRow>
            <TitleLeft>
              <span style={{ color: ACCENT }}><VideoIcon /></span>
              <Title>{t("video_tool.title")}</Title>
            </TitleLeft>
            {isMultiple && (
              <ProjectSelector projects={projects} value={projectId} onChange={setSelectedId} accent={ACCENT} />
            )}
          </TitleRow>
          <Subtitle>{t("video_tool.subtitle")}</Subtitle>
        </TitleArea>

        {!hasJobs && (
          <SetupSection>
            <DropArea {...getRootProps()} $active={isDragActive} $hasFile={!!file}>
              <input {...getInputProps()} />
              {file ? (
                <FilePreview>
                  <VideoFileOutlinedIcon style={{ fontSize: 28, color: ACCENT }} />
                  <FileInfo>
                    <FileName>{file.name}</FileName>
                    <FileSize>{(file.size / 1024 / 1024).toFixed(1)} MB</FileSize>
                  </FileInfo>
                  <ClearFile
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                    title={t("video_tool.remove_file")}
                  >
                    <CloseIcon style={{ fontSize: 16 }} />
                  </ClearFile>
                </FilePreview>
              ) : (
                <DropContent>
                  <CloudUploadOutlinedIcon
                    style={{ fontSize: 28, color: isDragActive ? ACCENT : "rgba(135,140,145,1)" }}
                  />
                  <DropLabel>
                    {isDragActive ? t("video_tool.drop_active") : t("video_tool.drop_file")}
                  </DropLabel>
                  <DropHint>{t("video_tool.drop_hint")}</DropHint>
                </DropContent>
              )}
            </DropArea>

            <LangGrid>
              <LangGroup>
                <LangLabel>{t("video_tool.source_language")}</LangLabel>
                <LanguageBtn
                  value={sourceLang}
                  setValue={setSourceLang}
                  placeholder={t("video_tool.select_language")}
                  valuesList={videoToolLanguages}
                  callLanguage
                />
              </LangGroup>

              <LangGroup>
                <LangLabel>{t("video_tool.target_languages", "Target languages (up to {max})", { max: maxTargetLanguages })}</LangLabel>
                {targetLangs.length < maxTargetLanguages && (
                  <LanguageBtn
                    value=""
                    setValue={addTargetLang}
                    placeholder={targetLangs.length ? t("video_tool.add_language", "Add a language") : t("video_tool.select_language")}
                    valuesList={availableLangs}
                    callLanguage
                  />
                )}
                {targetLangs.length > 0 && (
                  <LangChips>
                    {targetLangs.map((locale) => (
                      <LangChip key={locale}>
                        <span>{languageName(locale)}</span>
                        <ChipRemoveBtn
                          type="button"
                          onClick={() => removeTargetLang(locale)}
                          title={t("video_tool.remove_language", "Remove language")}
                        >
                          <CloseIcon style={{ fontSize: 13 }} />
                        </ChipRemoveBtn>
                      </LangChip>
                    ))}
                  </LangChips>
                )}
              </LangGroup>
            </LangGrid>

            {FEATURE_FLAGS.VIDEO_SUBTITLES && (
              <CheckboxGroup>
                <CheckboxRow onClick={() => setIncludeSubtitles((v) => !v)}>
                  <Checkbox
                    type="checkbox"
                    checked={includeSubtitles}
                    onChange={(e) => setIncludeSubtitles(e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <CheckboxLabel>{t("video_tool.with_subtitles")}</CheckboxLabel>
                  <InfoBubble title={t("video_tool.subtitles_tooltip")}>?</InfoBubble>
                </CheckboxRow>
                <CheckboxRow onClick={() => setIncludeSrtExport((v) => !v)}>
                  <Checkbox
                    type="checkbox"
                    checked={includeSrtExport}
                    onChange={(e) => setIncludeSrtExport(e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <CheckboxLabel>{t("video_tool.with_srt_export")}</CheckboxLabel>
                  <InfoBubble title={t("video_tool.srt_export_tooltip")}>?</InfoBubble>
                </CheckboxRow>
              </CheckboxGroup>
            )}

            <ContextGlossarySection>
              <ToggleRow onClick={() => setShowContextGlossary((v) => !v)}>
                <ToggleChevron $open={showContextGlossary}>›</ToggleChevron>
                <ToggleLabel>{t("video_tool.context_glossary_toggle")}</ToggleLabel>
              </ToggleRow>

              {showContextGlossary && (
                <ContextGlossaryBody>
                  <FieldGroup>
                    <FieldLabel>{t("video_tool.video_context_label")}</FieldLabel>
                    <ContextTextarea
                      value={videoContext}
                      onChange={(e) => setVideoContext(e.target.value.slice(0, MAX_VIDEO_CONTEXT_LENGTH))}
                      placeholder={t("video_tool.video_context_placeholder")}
                      maxLength={MAX_VIDEO_CONTEXT_LENGTH}
                      rows={3}
                    />
                    <CharCounter>{videoContext.length}/{MAX_VIDEO_CONTEXT_LENGTH}</CharCounter>
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel>{t("video_tool.glossary_label")}</FieldLabel>
                    <HelperText>{t("video_tool.glossary_helper")}</HelperText>

                    {glossary.length > 0 && (
                      <GlossaryRows>
                        {glossary.map((row) => (
                          <GlossaryRow key={row.id}>
                            <GlossaryInput
                              value={row.term}
                              onChange={(e) => updateGlossaryRow(row.id, "term", e.target.value)}
                              placeholder={t("video_tool.glossary_term_placeholder")}
                              maxLength={MAX_GLOSSARY_FIELD_LENGTH}
                            />
                            <GlossaryArrow>→</GlossaryArrow>
                            <GlossaryInput
                              value={row.translation}
                              onChange={(e) => updateGlossaryRow(row.id, "translation", e.target.value)}
                              placeholder={t("video_tool.glossary_target_placeholder")}
                              maxLength={MAX_GLOSSARY_FIELD_LENGTH}
                            />
                            <RemoveRowBtn
                              onClick={() => removeGlossaryRow(row.id)}
                              title={t("video_tool.glossary_remove_term")}
                              type="button"
                            >
                              <CloseIcon style={{ fontSize: 14 }} />
                            </RemoveRowBtn>
                          </GlossaryRow>
                        ))}
                      </GlossaryRows>
                    )}

                    <AddTermBtn
                      onClick={addGlossaryRow}
                      disabled={glossary.length >= MAX_GLOSSARY_ROWS}
                      type="button"
                    >
                      {t("video_tool.glossary_add_term")}
                    </AddTermBtn>
                  </FieldGroup>
                </ContextGlossaryBody>
              )}
            </ContextGlossarySection>

            <PrimaryBtn
              onClick={handleTranslate}
              disabled={!file || !sourceLang || targetLangs.length === 0}
            >
              {t("video_tool.translate")}
            </PrimaryBtn>
          </SetupSection>
        )}

        {hasJobs && (
          <QueueSection>
            <QueueList>
              {jobs.map((job) => (
                <QueueItem key={job.id} $isActive={isInFlight(job.status)}>
                  <QueueRow>
                    <QueueMeta>
                      <QueueFilename>{job.filename}</QueueFilename>
                      <QueueLangs>{job.sourceLang} → {job.targetLang}</QueueLangs>
                    </QueueMeta>
                    <QueueRight>
                      {job.status === "completed" && (
                        <CompletedBadge>
                          <CheckCircleOutlineIcon style={{ fontSize: 15, color: "#11B973" }} />
                          <span>{STATUS_LABELS.completed}</span>
                        </CompletedBadge>
                      )}
                      {(job.status === "failed" || job.status === "rejected") && (
                        <FailedBadge>
                          <ErrorOutlineIcon style={{ fontSize: 15, color: "rgba(238,80,80,1)" }} />
                          <span>{job.error_message || STATUS_LABELS[job.status]}</span>
                        </FailedBadge>
                      )}
                      {isInFlight(job.status) && (
                        <ActiveText>{STATUS_LABELS[job.status] || job.status}</ActiveText>
                      )}
                      {job.status === "completed" && job.download_url && (
                        <DownloadIconBtn onClick={() => downloadJob(job.download_url)} title={t("video_tool.download_title")}>
                          <FileDownloadOutlinedIcon style={{ fontSize: 18 }} />
                        </DownloadIconBtn>
                      )}
                      {job.status === "completed" && job.srt_download_url && (
                        <DownloadIconBtn onClick={() => downloadJob(job.srt_download_url)} title={t("video_tool.download_srt")}>
                          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "-0.3px" }}>SRT</span>
                        </DownloadIconBtn>
                      )}
                    </QueueRight>
                  </QueueRow>

                  {isInFlight(job.status) && (
                    <>
                      <ProgressTrack>
                        <ProgressFill $status={job.status} />
                      </ProgressTrack>
                      <ProcessingNote>{t("video_tool.processing_note")}</ProcessingNote>
                    </>
                  )}
                </QueueItem>
              ))}
            </QueueList>

            {allDone && (
              <QueueFooter>
                <SuccessMessage>
                  <CheckCircleOutlineIcon style={{ fontSize: 18, color: "#11B973" }} />
                  {t("video_tool.queue_complete")}
                </SuccessMessage>
                <GhostBtn onClick={handleReset}>{t("video_tool.translate_another")}</GhostBtn>
              </QueueFooter>
            )}
          </QueueSection>
        )}
      </Content>
    </PageWrapper>
  );
};

export default VideoTranslationToolPage;

const shimmer = keyframes`
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  height: 100%;
  width: 100%;
  padding: 48px 24px 64px;
  background: ${({ theme }) => theme.menu.background};
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  @media (max-width: 480px) { padding: 24px 16px 48px; }
`;

const Content = styled.div`
  max-width: 680px;
  margin: 0 auto;
  animation: ${fadeUp} 0.35s cubic-bezier(0.16, 1, 0.3, 1);
`;

const NoAccessCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 64px 32px;
  text-align: center;
`;

const NoAccessIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: ${ACCENT}1A;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
`;

const NoAccessTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin: 0;
`;

const NoAccessMessage = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  margin: 0;
  max-width: 360px;
  line-height: 1.6;
`;

const NoAccessLink = styled.a`
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${ACCENT};
  text-decoration: none;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const TitleArea = styled.div`
  margin-bottom: 32px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const TitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  letter-spacing: -0.3px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  margin: 0;
  color: ${({ theme }) => theme.menu.infoText};
  padding-left: 32px;
`;

const SetupSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DropArea = styled.div`
  border: 1.5px dashed
    ${({ $active, $hasFile }) =>
      $active ? ACCENT : $hasFile ? `${ACCENT}59` : "rgba(215,214,238,1)"};
  border-radius: 10px;
  padding: ${({ $hasFile }) => ($hasFile ? "16px 18px" : "32px 18px")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, background 0.2s;
  background: ${({ $active }) => ($active ? `${ACCENT}08` : "transparent")};
  min-height: 120px;
  &:hover {
    border-color: ${ACCENT}80;
    background: ${ACCENT}05;
  }
`;

const DropContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const DropLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  text-align: center;
`;

const DropHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.infoText};
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const FileInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const FileName = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.infoText};
  margin-top: 1px;
`;

const ClearFile = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.menu.infoText};
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  flex-shrink: 0;
  &:hover { color: rgba(238,80,80,1); }
`;

const LangGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const LangGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LangChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const LangChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  background: ${ACCENT}14;
  border: 1px solid ${ACCENT}40;
  border-radius: 14px;
  padding: 3px 4px 3px 10px;
`;

const ChipRemoveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 50%;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  &:hover { color: rgba(238,80,80,1); background: rgba(238,80,80,0.08); }
`;

const LangLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const PrimaryBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  border: none;
  border-radius: 9px;
  color: #fff;
  background: ${ACCENT};
  cursor: pointer;
  transition: opacity 0.15s, transform 0.1s;
  &:hover:not(:disabled) { opacity: 0.88; }
  &:active:not(:disabled) { transform: scale(0.98); }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
`;

const GhostBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 9px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  background: transparent;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  &:hover {
    background: ${ACCENT}0A;
    border-color: ${ACCENT}4D;
  }
`;

const QueueSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${fadeUp} 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

const QueueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const QueueItem = styled.div`
  padding: 14px 16px;
  border-radius: 8px;
  background: ${({ $isActive }) => ($isActive ? `${ACCENT}08` : "transparent")};
  border: 1px solid
    ${({ $isActive, theme }) => ($isActive ? `${ACCENT}1F` : theme.sidebar.borderColor)};
  transition: background 0.2s, border-color 0.2s;
`;

const QueueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const QueueMeta = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const QueueFilename = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QueueLangs = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.infoText};
  margin-top: 2px;
`;

const QueueRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 1 auto;
  min-width: 0;
`;

const ActiveText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${ACCENT};
`;

const CompletedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: #11b973;
`;

const FailedBadge = styled.span`
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  color: rgba(238,80,80,1);
  overflow-wrap: anywhere;

  svg {
    flex-shrink: 0;
  }
`;

const DownloadIconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: none;
  cursor: pointer;
  color: ${ACCENT};
  border-radius: 5px;
  transition: background 0.15s;
  &:hover { background: ${ACCENT}1A; }
`;

const ProgressTrack = styled.div`
  height: 4px;
  border-radius: 2px;
  background: ${ACCENT}1A;
  overflow: hidden;
  margin-top: 10px;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(90deg, ${ACCENT} 0%, #4DD0C4 50%, ${ACCENT} 100%);
  background-size: 200% 100%;
  animation: ${shimmer} 2s linear infinite;
  width: ${({ $status }) => ($status === "uploading" ? "25%" : $status === "submitted" ? "50%" : "80%")};
  transition: width 0.8s cubic-bezier(0.16, 1, 0.3, 1);
`;

const QueueFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding-top: 8px;
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: ${ACCENT};
  cursor: pointer;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const InfoBubble = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.sidebar.borderColor};
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: default;
  flex-shrink: 0;
  position: relative;
  &:hover::after {
    content: attr(title);
    position: absolute;
    left: 22px;
    top: 50%;
    transform: translateY(-50%);
    background: ${({ theme }) => theme.menu.background};
    border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.menu.secondaryTextColor};
    white-space: normal;
    max-width: 240px;
    width: max-content;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    pointer-events: none;
  }
`;

const ProcessingNote = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.infoText};
  margin: 6px 0 0;
  line-height: 1.5;
`;

const ContextGlossarySection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  padding-top: 16px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
`;

const ToggleChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  transition: transform 0.15s;
  transform: rotate(${({ $open }) => ($open ? "90deg" : "0deg")});
`;

const ToggleLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const ContextGlossaryBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 16px;
  animation: ${fadeUp} 0.25s cubic-bezier(0.16, 1, 0.3, 1);
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const HelperText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.infoText};
  margin: 0 0 4px;
  line-height: 1.5;
`;

const ContextTextarea = styled.textarea`
  font-family: inherit;
  font-size: 13px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  background: transparent;
  border: 1px solid rgba(215,214,238,1);
  border-radius: 8px;
  padding: 10px 12px;
  resize: vertical;
  min-height: 64px;
  outline: none;
  &:focus { border-color: ${ACCENT}80; }
  &::placeholder { color: ${({ theme }) => theme.menu.infoText}; }
`;

const CharCounter = styled.span`
  align-self: flex-end;
  font-size: 11px;
  color: ${({ theme }) => theme.menu.infoText};
`;

const GlossaryRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GlossaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GlossaryInput = styled.input`
  flex: 1;
  min-width: 0;
  font-family: inherit;
  font-size: 13px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  background: transparent;
  border: 1px solid rgba(215,214,238,1);
  border-radius: 8px;
  padding: 8px 10px;
  outline: none;
  &:focus { border-color: ${ACCENT}80; }
  &::placeholder { color: ${({ theme }) => theme.menu.infoText}; }
`;

const GlossaryArrow = styled.span`
  flex-shrink: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.menu.infoText};
`;

const RemoveRowBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme }) => theme.menu.infoText};
  border-radius: 6px;
  flex-shrink: 0;
  &:hover { color: rgba(238,80,80,1); background: rgba(238,80,80,0.08); }
`;

const AddTermBtn = styled.button`
  align-self: flex-start;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${ACCENT};
  background: ${ACCENT}0F;
  border: 1px solid ${ACCENT}40;
  border-radius: 7px;
  padding: 6px 12px;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
  &:hover:not(:disabled) { background: ${ACCENT}1A; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
